import type { User as OidcUser, UserManager } from 'oidc-client-ts'

import {
  KeycloakTokenResponse,
  oidcUserFromKeycloakTokens,
} from '@/auth/keycloakLegacyBridgeLogin'
import { getKeycloakUserManager } from '@/auth/keycloakUserManager'
import { env } from '@/config/env'

/** Set when impersonation succeeds; survives access-token refresh if `azp` is missing in the JWT. */
export const BROWSER_IMPERSONATION_SESSION_KEY = 'communo_impersonation_session'

export function markBrowserImpersonationSession(): void {
  try {
    sessionStorage.setItem(BROWSER_IMPERSONATION_SESSION_KEY, '1')
  } catch {
    //
  }
}

export function clearBrowserImpersonationSession(): void {
  try {
    sessionStorage.removeItem(BROWSER_IMPERSONATION_SESSION_KEY)
  } catch {
    //
  }
}

export function hasBrowserImpersonationSessionFlag(): boolean {
  try {
    return sessionStorage.getItem(BROWSER_IMPERSONATION_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * While impersonating, leaving /admin triggers a JWT + currentUser update before navigation;
 * RequireAuth would briefly show 403. This flag swaps that for a loader until the flow finishes.
 */
const BROWSER_IMPERSONATION_ADMIN_EXIT_KEY = 'communo_impersonation_admin_exit'

export function markImpersonationAdminExitPending(): void {
  try {
    sessionStorage.setItem(BROWSER_IMPERSONATION_ADMIN_EXIT_KEY, '1')
  } catch {
    //
  }
}

export function clearImpersonationAdminExitPending(): void {
  try {
    sessionStorage.removeItem(BROWSER_IMPERSONATION_ADMIN_EXIT_KEY)
  } catch {
    //
  }
}

export function hasImpersonationAdminExitPending(): boolean {
  try {
    return sessionStorage.getItem(BROWSER_IMPERSONATION_ADMIN_EXIT_KEY) === '1'
  } catch {
    return false
  }
}

/** True when the SPA should call the backend exchange-refresh endpoint (not Keycloak direct). */
export function shouldUseExchangeBackendRefresh(
  user: OidcUser | null | undefined
): boolean {
  return Boolean(
    user?.refresh_token &&
    (isExchangeIssuedOidcUser(user) || hasBrowserImpersonationSessionFlag())
  )
}

/** Align with KEYCLOAK_TOKEN_EXCHANGE_CLIENT_ID / bootstrap-realm.sh */
export function getKeycloakTokenExchangeClientId(): string {
  const v = env.KEYCLOAK_TOKEN_EXCHANGE_CLIENT_ID
  if (typeof v === 'string' && v !== '') {
    return v
  }
  return 'communo-token-exchange'
}

function decodeJwtPayload(
  jwt: string | undefined
): Record<string, unknown> | undefined {
  if (!jwt || typeof jwt !== 'string') {
    return undefined
  }
  const parts = jwt.split('.')
  if (parts.length < 2) {
    return undefined
  }
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const json = atob(padded)
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return undefined
  }
}

export function isExchangeIssuedAccessToken(
  accessToken: string | null | undefined
): boolean {
  if (!accessToken) {
    return false
  }
  const p = decodeJwtPayload(accessToken)
  const azp = p?.azp
  return typeof azp === 'string' && azp === getKeycloakTokenExchangeClientId()
}

export function isExchangeIssuedOidcUser(
  user: OidcUser | null | undefined
): boolean {
  return isExchangeIssuedAccessToken(user?.access_token)
}

export async function fetchRefreshedTokensFromExchangeBackend(
  refreshToken: string
): Promise<KeycloakTokenResponse> {
  const apiBase = (env.API_URL as string).replace(/\/$/, '')
  const res = await fetch(`${apiBase}/login/keycloak-exchange-refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!res.ok) {
    let msg = 'Keycloak exchange refresh failed.'
    try {
      const j = (await res.json()) as {
        error_description?: string
        message?: string
        error?: string
      }
      if (typeof j.error_description === 'string' && j.error_description) {
        msg = j.error_description
      } else if (typeof j.message === 'string' && j.message) {
        msg = j.message
      } else if (typeof j.error === 'string' && j.error) {
        msg = j.error
      }
    } catch {
      //
    }
    throw new Error(msg)
  }

  return (await res.json()) as KeycloakTokenResponse
}

export async function applyExchangeRefreshToUserManager(
  um: UserManager,
  tokens: KeycloakTokenResponse,
  previousUser: OidcUser
): Promise<OidcUser> {
  const next = oidcUserFromKeycloakTokens({
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token ?? previousUser.refresh_token,
    expires_in: tokens.expires_in,
    id_token: tokens.id_token,
    token_type: tokens.token_type,
    scope: tokens.scope,
    session_state: tokens.session_state ?? previousUser.session_state,
  })
  await um.storeUser(next)
  await um.events.load(next)
  return next
}

/**
 * Refresh tokens using the confidential exchange client (browser cannot call Keycloak refresh for azp=exchange).
 */
export async function tryRenewExchangeSession(
  um: UserManager
): Promise<OidcUser | null> {
  const u = await um.getUser()
  if (!u || !shouldUseExchangeBackendRefresh(u) || !u.refresh_token) {
    return null
  }
  const raw = await fetchRefreshedTokensFromExchangeBackend(u.refresh_token)
  if (!raw.access_token) {
    return null
  }
  return applyExchangeRefreshToUserManager(um, raw, u)
}

/** Stop iframe/SPA silent renew for exchange sessions; restore for normal OIDC. */
export function configureSilentRenewForOidcUser(
  u: OidcUser | null | undefined
): void {
  const um = getKeycloakUserManager()
  if (
    isExchangeIssuedOidcUser(u) ||
    (Boolean(u?.access_token) && hasBrowserImpersonationSessionFlag())
  ) {
    um.stopSilentRenew()
  } else {
    um.startSilentRenew()
  }
}
